import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TranslationsTab = ({ language }) => {
  const [translations, setTranslations] = useState([
    {
      id: 1,
      key: 'home.hero.title',
      value_en: 'Premium African Green Coffee Beans',
      value_zh: '优质非洲生咖啡豆',
      category: 'Home',
      last_updated: new Date('2024-11-25T10:00:00')
    },
    {
      id: 2,
      key: 'home.hero.subtitle',
      value_en: 'Direct from verified suppliers with real farm data',
      value_zh: '来自经过验证的供应商，提供真实农场数据',
      category: 'Home',
      last_updated: new Date('2024-11-25T10:00:00')
    },
    {
      id: 3,
      key: 'beans.filter.country',
      value_en: 'Filter by Country',
      value_zh: '按国家筛选',
      category: 'Beans',
      last_updated: new Date('2024-11-26T14:30:00')
    },
    {
      id: 4,
      key: 'beans.filter.processing',
      value_en: 'Processing Method',
      value_zh: '处理方式',
      category: 'Beans',
      last_updated: new Date('2024-11-26T14:30:00')
    },
    {
      id: 5,
      key: 'order.form.title',
      value_en: 'Request Order',
      value_zh: '订单申请',
      category: 'Order',
      last_updated: new Date('2024-11-27T09:15:00')
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const filteredTranslations = translations?.filter(trans => {
    const matchesSearch = trans?.key?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         trans?.value_en?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         trans?.value_zh?.includes(searchTerm);
    const matchesCategory = filterCategory === 'all' || trans?.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(translations.map(t => t.category))];

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <Input
            type="search"
            placeholder={language === 'en' ? 'Search translations...' : '搜索翻译...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full sm:w-64"
          />
          <div className="flex gap-2">
            <label htmlFor="translation-upload">
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={() => document.getElementById('translation-upload')?.click()}
              >
                {language === 'en' ? 'Upload CSV/JSON' : '上传 CSV/JSON'}
              </Button>
            </label>
            <input
              id="translation-upload"
              type="file"
              accept=".csv,.json"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button variant="default" iconName="Plus" iconPosition="left">
              {language === 'en' ? 'Add Translation' : '添加翻译'}
            </Button>
          </div>
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-48"
        >
          <option value="all">{language === 'en' ? 'All Categories' : '所有类别'}</option>
          {categories?.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>
      {isUploading && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Upload" size={20} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">
              {language === 'en' ? 'Processing translation file...' : '处理翻译文件...'}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {language === 'en' ? 'Validating and importing translations...' : '验证并导入翻译...'}
          </p>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Key' : '键'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'English' : '英文'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Chinese' : '中文'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Category' : '类别'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Last Updated' : '最后更新'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Actions' : '操作'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredTranslations?.map((trans) => (
                <tr key={trans?.id} className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 py-3">
                    <code className="text-xs bg-muted px-2 py-1 rounded text-primary">{trans?.key}</code>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground max-w-xs truncate">{trans?.value_en}</td>
                  <td className="px-4 py-3 text-sm text-foreground max-w-xs truncate">{trans?.value_zh}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-accent/10 text-accent-foreground text-xs font-medium">
                      {trans?.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(trans?.last_updated)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                        aria-label="Edit translation"
                      >
                        <Icon name="Edit2" size={16} color="var(--color-primary)" />
                      </button>
                      <button
                        className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                        aria-label="Delete translation"
                      >
                        <Icon name="Trash2" size={16} color="var(--color-error)" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredTranslations?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">{language === 'en' ? 'No translations found' : '未找到翻译'}</p>
        </div>
      )}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-foreground">
              {language === 'en' ? 'Translation File Format' : '翻译文件格式'}
            </h4>
            <p className="text-sm text-muted-foreground">
              {language === 'en' ?'Upload CSV or JSON files with columns: key, value_en, value_zh, category. Changes will be applied instantly across the platform.' :'上传包含以下列的 CSV 或 JSON 文件：key、value_en、value_zh、category。更改将立即应用于整个平台。'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationsTab;